import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
} from '@mui/material';
import { PictureAsPdf, Info, CheckCircle } from '@mui/icons-material';
import { PageHeader, Loading, ErrorMessage } from './common';
import { apiService } from '../services/api';

const Reports = () => {
  const [candidateId, setCandidateId] = useState('');
  const [toolId, setToolId] = useState(7);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    if (!candidateId.trim()) {
      setError('Please enter a candidate ID');
      return;
    }

    try {
      setGenerating(true);
      setError(null);
      const response = await apiService.candidates.generateReport({
        candidate_id: parseInt(candidateId),
        tool_id: toolId
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `candidate_${candidateId}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating report:', err);
      if (err.response?.status === 404) {
        setError('No evaluation data found for this candidate and tool combination');
      } else {
        setError('Failed to generate report. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Generate Candidate Reports"
        subtitle="Create detailed PDF reports containing evaluation results, scores, and analysis"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box component="form" onSubmit={handleGenerateReport} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Candidate ID"
                  type="number"
                  value={candidateId}
                  onChange={(e) => setCandidateId(e.target.value)}
                  placeholder="Enter candidate ID (e.g., 123)"
                  required
                  fullWidth
                />

                <TextField
                  select
                  label="Tool ID"
                  value={toolId}
                  onChange={(e) => setToolId(Number(e.target.value))}
                  fullWidth
                >
                  <MenuItem value={7}>Tool 7</MenuItem>
                  <MenuItem value={8}>Tool 8</MenuItem>
                  <MenuItem value={9}>Tool 9</MenuItem>
                </TextField>

                {error && <ErrorMessage message={error} onRetry={() => handleGenerateReport({ preventDefault: () => {} })} />}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={generating || !candidateId.trim()}
                  startIcon={<PictureAsPdf />}
                >
                  {generating ? 'Generating Report...' : 'Generate PDF Report'}
                </Button>

                {generating && <Loading message="Generating report..." inline />}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ bgcolor: 'background.default', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Report Information
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Info color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>What's Included</Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                    <li>Candidate evaluation scores</li>
                    <li>Detailed aspect breakdowns</li>
                    <li>Comparison with ground truth</li>
                    <li>Performance analysis</li>
                  </Box>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircle color="success" />
                    <Typography variant="subtitle1" fontWeight={600}>Requirements</Typography>
                  </Box>
                  <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                    <li>Candidate must be evaluated</li>
                    <li>Valid tool ID must be specified</li>
                    <li>Evaluation data must exist</li>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;
